import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Loader2,
  FileBarChart,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from 'recharts';
import { formatCompact } from '../lib/chartFormatters';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { PersonalLayout } from '../components/PersonalLayout';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { useEntityStore, usePortfolioPrefsStore, PERSONAL_VIEW_OPTIONS } from '../store';
import { convertAmount } from '../lib/currencyConvert';
import { reportsAPI, exchangeRatesAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';
import { COST_BUCKET_LABELS, OPERATIONAL_SUBGROUP_LABELS } from '../lib/costTaxonomy';
import { buildNetProfitChartData } from '../lib/pnlNetProfitChart';
import { PnlNetProfitChart } from '../components/dashboard/PnlNetProfitChart';
import { entityPnlYtdRows, entityPnlRow, sumRows } from '../lib/entityPnlRows';
import { TaxPaidPanel } from '../components/TaxPaidPanel';

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$', DKK: 'kr' };

const formatAmount = (value, currency = 'GBP') => {
  const sym = CURRENCY_SYMBOLS[currency] ?? currency + ' ';
  const sign = value < 0 ? '-' : '';
  return `${sign}${sym}${Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const signedAmount = (value, currency = 'GBP') =>
  `${value >= 0 ? '+' : '-'}${formatAmount(Math.abs(value), currency)}`;

/**
 * Convert a raw value out of a monthly report (native currency, the report's
 * own `currency` field) into the user's toggle-selected display currency.
 * /reports/entity-pnl (this page's data source, routed through
 * services/entity_pnl.py) does not carry an fx_rates_used field, so this
 * uses live rates fetched from GET /fx/rates instead (see fetchFxRates in
 * the page component below) -- same approach as InvoicesPage.jsx's aggregate.
 * Shared by all three sections (P&L, Revenue, Costs) instead of each
 * re-deriving reportCurrency/fmt.
 */
const convertReportValue = (report, toggleCurrency, fxRates, value) => {
  const reportCurrency = report?.currency ?? 'GBP';
  return convertAmount(value, reportCurrency, toggleCurrency, fxRates);
};

const categoryLabels = {
  ...COST_BUCKET_LABELS,
  cleaning: OPERATIONAL_SUBGROUP_LABELS.cleaning,
  repairs: OPERATIONAL_SUBGROUP_LABELS.repairs,
  other_operational: OPERATIONAL_SUBGROUP_LABELS.other_operational,
};

/** Parse string-encoded Decimal to number, defaulting to 0 for missing/empty. */
const n = (v) => Number(v) || 0;

const SOURCE_BADGE = {
  label: 'Portfolio',
  className: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
};

const PIE_COLORS = [
  '#EF4444', '#F97316', '#8B5CF6', '#3B82F6',
  '#EC4899', '#EAB308', '#06B6D4', '#64748B',
  '#10B981', '#F43F5E', '#6366F1', '#14B8A6',
];

const SECTION_TABS = [
  { key: 'pnl', label: 'Profit & Loss' },
  { key: 'revenue', label: 'Revenues' },
  { key: 'costs', label: 'Costs' },
];

function SummaryCard({ label, value, icon: Icon, color, sub, change }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <div className={cn('p-2 rounded-lg', color)}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className={cn('text-2xl font-bold font-mono', sub)}>{value}</p>
            {change !== null && change !== undefined && (
              <p className={cn(
                'text-xs font-medium mt-0.5',
                change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              )}>
                {change >= 0 ? '+' : ''}{change.toFixed(0)}% YoY
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChartTooltip({ active, payload, label, currency = 'GBP' }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex justify-between gap-4">
          <span style={{ color: entry.color }}>{entry.name}</span>
          <span className="font-mono">{formatAmount(entry.value, currency)}</span>
        </div>
      ))}
    </div>
  );
}

// ── P&L section ──

function PersonalPnlContent({ report, priorYear, year, setYear, isRefreshing, onRefresh, toggleCurrency, fxRates }) {
  const summary = report?.summary;
  const priorSummary = priorYear?.summary;
  const cv = (v) => convertReportValue(report, toggleCurrency, fxRates, v);
  const fmt = (v) => formatAmount(cv(v), toggleCurrency);
  const signedFmt = (v) => signedAmount(cv(v), toggleCurrency);

  const yoyChange = (current, prior) => {
    if (!prior || prior === 0) return null;
    return ((current - prior) / Math.abs(prior)) * 100;
  };

  // One basis (Fan, 2026-10-25): cards and table total are the sum of the
  // month rows shown (actuals to date), the same figures as the Personal
  // Dashboard. Prior year is compared over the same months.
  const ytdTot = sumRows(entityPnlYtdRows(report?.months, year));
  const nowMonthNum = new Date().getMonth() + 1;
  const priorSameMonths = (priorYear?.months || []).filter((m) => {
    const [, mm] = m.month.split('-').map(Number);
    return year < new Date().getFullYear() || mm <= nowMonthNum;
  });
  const priorTot = sumRows(entityPnlYtdRows(priorSameMonths, year - 1));
  const totalRevenue = ytdTot.revenue;
  const totalCosts = ytdTot.costs;
  const netResult = ytdTot.net;
  const priorRevenue = priorSummary ? priorTot.revenue : 0;
  const priorCosts = priorSummary ? priorTot.costs : 0;
  const priorNet = priorSummary ? priorTot.net : 0;

  const chartData = useMemo(() => {
    if (!report) return [];
    const now = new Date();
    return report.months
      .filter((m) => {
        const [, mm] = m.month.split('-').map(Number);
        if (year < now.getFullYear()) return true;
        if (year > now.getFullYear()) return false;
        return mm <= now.getMonth() + 1;
      })
      .map((m) => ({
        month: m.label.slice(0, 3),
        revenue: cv(n(m.gross_revenue)),
        expenses: cv(n(m.costs)),
        net_profit: cv(n(m.net_result)),
      }));
  }, [report, year, toggleCurrency, fxRates]);

  // Net Profit (monthly) chart data (dashboards round 3, commit 6), memoized
  // like this same function's own chartData above rather than rebuilt
  // inline in JSX on every unrelated re-render. Converted to the toggle
  // currency before handing off to buildNetProfitChartData, same as the
  // entity-scope branch of ProfitLossPage does for its analogous chart.
  const netProfitChartData = useMemo(() => {
    const convertedMonths = (report?.months || []).map((m) => ({
      month: m.month,
      net_result: m.net_result != null ? cv(n(m.net_result)) : null,
      net_result_actual: m.net_result_actual != null ? cv(n(m.net_result_actual)) : null,
      net_result_forward: m.net_result_forward != null ? cv(n(m.net_result_forward)) : null,
    }));
    return buildNetProfitChartData({ year, months: convertedMonths });
  }, [report, year, toggleCurrency, fxRates]);

  return (
    <div className="flex flex-col gap-6">
      {/* Year nav + refresh + source badge */}
      <div className="flex items-center gap-3 justify-end">
        <Badge className={cn('text-xs px-2 py-0.5', SOURCE_BADGE.className)}>{SOURCE_BADGE.label}</Badge>
        {report?.property_count != null && (
          <span className="text-xs text-muted-foreground">{report.property_count} properties</span>
        )}
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={isRefreshing}>
          <RefreshCw className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')} />
          Refresh
        </Button>
        <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-card">
          <button onClick={() => setYear((y) => y - 1)} className="p-1 rounded hover:bg-muted transition-colors">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="font-semibold w-12 text-center">{year}</span>
          <button
            onClick={() => setYear((y) => y + 1)}
            disabled={year >= new Date().getFullYear()}
            className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pnl-summary-cards">
          <SummaryCard
            label="Revenue (year to date)"
            value={fmt(totalRevenue)}
            icon={TrendingUp}
            color="bg-green-100 dark:bg-green-900/30"
            sub="text-green-600 dark:text-green-400"
            change={yoyChange(totalRevenue, priorRevenue)}
          />
          <SummaryCard
            label="Total Costs (year to date)"
            value={fmt(totalCosts)}
            icon={TrendingDown}
            color="bg-red-100 dark:bg-red-900/30"
            sub="text-red-600 dark:text-red-400"
            change={yoyChange(totalCosts, priorCosts)}
          />
          <SummaryCard
            label="Net Result (year to date)"
            value={signedFmt(netResult)}
            icon={DollarSign}
            color={netResult >= 0 ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-red-100 dark:bg-red-900/30'}
            sub={netResult >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}
            change={yoyChange(netResult, priorNet)}
          />
        </div>
      )}

      {/* Net Profit (monthly) chart (dashboards round 3, commit 6): directly
          below the stat cards, from the canonical get_personal_pnl months
          array -- personal scope already IS the All Personal aggregate
          (get_personal_pnl sums every PERSONAL_ENTITY_IDS member), so this
          single insertion covers both. */}
      <PnlNetProfitChart
        chartData={netProfitChartData}
        displayCurrency={toggleCurrency}
      />

      {/* Monthly trend chart */}
      {report && chartData.some((d) => d.revenue > 0 || d.expenses > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Monthly Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chartData} margin={{ top: 30, right: 10, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false}
                  tickFormatter={(v) => `${CURRENCY_SYMBOLS[toggleCurrency] ?? toggleCurrency}${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip content={<ChartTooltip currency={toggleCurrency} />} />
                <Legend />
                <Bar dataKey="revenue" name="Revenue" fill="#10B981" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="revenue" position="top" formatter={(v) => formatCompact(v, toggleCurrency)} style={{ fontSize: 10, fontWeight: 600, fill: '#10B981' }} />
                </Bar>
                <Bar dataKey="expenses" name="Costs" fill="#EF4444" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="expenses" position="top" formatter={(v) => formatCompact(v, toggleCurrency)} style={{ fontSize: 10, fontWeight: 600, fill: '#EF4444' }} />
                </Bar>
                <Line type="monotone" dataKey="net_profit" name="Net Result" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3, fill: '#3B82F6' }}>
                  <LabelList dataKey="net_profit" position="bottom" formatter={(v) => formatCompact(v, toggleCurrency)} style={{ fontSize: 10, fontWeight: 600, fill: '#3B82F6' }} offset={8} />
                </Line>
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Monthly breakdown table */}
      {report ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Total Costs</TableHead>
                  <TableHead className="text-right">Net Result</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.months.filter((m) => {
                  const now = new Date();
                  const [, mm] = m.month.split('-').map(Number);
                  if (year < now.getFullYear()) return true;
                  if (year > now.getFullYear()) return false;
                  return mm <= now.getMonth() + 1;
                }).map((m) => {
                  const row = entityPnlRow(m);
                  const rev = row.revenue;
                  const cost = row.costs;
                  const net = row.net;
                  const hasData = rev > 0 || cost > 0;
                  const margin = rev > 0 ? (net / rev) * 100 : null;
                  const now = new Date();
                  const [, mm] = m.month.split('-').map(Number);
                  const isCurrentMonth = mm === now.getMonth() + 1 && year === now.getFullYear();
                  return (
                    <TableRow key={m.month} className={cn(isCurrentMonth && 'bg-primary/5')}>
                      <TableCell className="font-medium">
                        {m.label}
                        {isCurrentMonth && (
                          <Badge className="ml-2 text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">Now</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                        {hasData ? fmt(rev) : '\u2014'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                        {hasData ? fmt(cost) : '\u2014'}
                      </TableCell>
                      <TableCell className={cn('text-right font-mono font-semibold', hasData && (net >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'))}>
                        {hasData ? signedFmt(net) : '\u2014'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">
                        {hasData && margin !== null ? `${margin.toFixed(1)}%` : '\u2014'}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {summary && (
                  <TableRow className="border-t-2 font-semibold bg-muted/30">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right font-mono text-green-600 dark:text-green-400">{fmt(totalRevenue)}</TableCell>
                    <TableCell className="text-right font-mono text-red-600 dark:text-red-400">{fmt(totalCosts)}</TableCell>
                    <TableCell className={cn('text-right font-mono', netResult >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>{signedFmt(netResult)}</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">
                      {totalRevenue > 0 ? `${((netResult / totalRevenue) * 100).toFixed(1)}%` : '\u2014'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-16 text-center">
            <FileBarChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Data for {year}</h3>
            <p className="text-muted-foreground">Sync your integrations to populate financial data.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Revenue section ──

function PersonalRevenueContent({ report, year, setYear, isRefreshing, onRefresh, toggleCurrency, fxRates }) {
  const cv = (v) => convertReportValue(report, toggleCurrency, fxRates, v);
  const fmt = (v) => formatAmount(cv(v), toggleCurrency);

  const revenueMonths = useMemo(() => {
    if (!report) return [];
    const now = new Date();
    return report.months
      .filter((m) => {
        const [, mm] = m.month.split('-').map(Number);
        if (year < now.getFullYear()) return true;
        if (year > now.getFullYear()) return false;
        return mm <= now.getMonth() + 1;
      })
      .map((m) => {
        const rev = n(m.gross_revenue);
        const [, mm] = m.month.split('-').map(Number);
        return {
          month_label: m.label,
          month: m.month,
          monthNum: mm,
          revenue: rev,
          hasData: rev > 0,
        };
      });
  }, [report, year]);

  const totalRevenue = revenueMonths.reduce((s, m) => s + m.revenue, 0);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3 justify-end">
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={isRefreshing}>
          <RefreshCw className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')} />
          Refresh
        </Button>
        <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-card">
          <button onClick={() => setYear((y) => y - 1)} className="p-1 rounded hover:bg-muted transition-colors">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="font-semibold w-12 text-center">{year}</span>
          <button onClick={() => setYear((y) => y + 1)} disabled={year >= new Date().getFullYear()}
            className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {report && revenueMonths.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Monthly Revenue</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {revenueMonths.map((m) => {
                  const now = new Date();
                  const isCurrentMonth = m.monthNum === now.getMonth() + 1 && year === now.getFullYear();
                  return (
                    <TableRow key={m.month} className={cn(isCurrentMonth && 'bg-primary/5')}>
                      <TableCell className="font-medium">
                        {m.month_label}
                        {isCurrentMonth && <Badge className="ml-2 text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">Now</Badge>}
                      </TableCell>
                      <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                        {m.hasData ? fmt(m.revenue) : '\u2014'}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className="border-t-2 font-semibold bg-muted/30">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right font-mono text-green-600 dark:text-green-400">{fmt(totalRevenue)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-16 text-center">
            <FileBarChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No revenue data for {year}</h3>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Costs section ──

function PersonalCostsContent({ report, year, setYear, isRefreshing, onRefresh, toggleCurrency, fxRates }) {
  const cv = (v) => convertReportValue(report, toggleCurrency, fxRates, v);
  const fmt = (v) => formatAmount(cv(v), toggleCurrency);

  // Tax paid by the personal entities (Fan, 2026-10-27): same rules and
  // endpoint as the business Costs tab. Shown beside the costs, not in them.
  const [taxPaid, setTaxPaid] = useState(null);
  useEffect(() => {
    let alive = true;
    Promise.resolve()
      .then(() => reportsAPI.getTaxPaid({ year, scope: 'personal' }))
      .then((res) => { if (alive) setTaxPaid(res?.data || null); })
      .catch(() => { if (alive) setTaxPaid(null); });
    return () => { alive = false; };
  }, [year]);
  const fmtTax = (v) => formatAmount(convertAmount(v, taxPaid?.currency || 'GBP', toggleCurrency, fxRates), toggleCurrency);

  const COST_FIELDS = ['ota_commission', 'management_fees', 'cleaning', 'repairs', 'other_operational', 'financing', 'unclassified'];

  const yearCategories = useMemo(() => {
    if (!report) return [];
    const totals = {};
    report.months.forEach((m) => {
      COST_FIELDS.forEach((key) => {
        const v = n(m[key]);
        if (v > 0) totals[key] = (totals[key] ?? 0) + v;
      });
    });
    return Object.entries(totals)
      .sort(([, a], [, b]) => b - a)
      .map(([cat, amount]) => ({
        name: categoryLabels[cat] ?? cat,
        value: Math.round(amount),
        key: cat,
      }));
  }, [report]);

  const totalSpend = yearCategories.reduce((s, c) => s + c.value, 0);

  const costMonths = useMemo(() => {
    if (!report) return [];
    const now = new Date();
    return report.months
      .filter((m) => {
        const [, mm] = m.month.split('-').map(Number);
        if (year < now.getFullYear()) return true;
        if (year > now.getFullYear()) return false;
        return mm <= now.getMonth() + 1;
      })
      .map((m) => {
        const cost = n(m.costs);
        const [, mm] = m.month.split('-').map(Number);
        return {
          month_label: m.label,
          month: m.month,
          monthNum: mm,
          expenses: cost,
          hasData: cost > 0,
        };
      });
  }, [report, year]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3 justify-end">
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={isRefreshing}>
          <RefreshCw className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')} />
          Refresh
        </Button>
        <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-card">
          <button onClick={() => setYear((y) => y - 1)} className="p-1 rounded hover:bg-muted transition-colors">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="font-semibold w-12 text-center">{year}</span>
          <button onClick={() => setYear((y) => y + 1)} disabled={year >= new Date().getFullYear()}
            className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {yearCategories.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle className="text-lg">Cost Category Distribution</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={yearCategories} cx="50%" cy="50%" innerRadius={70} outerRadius={120} dataKey="value" paddingAngle={2}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {yearCategories.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => fmt(value)} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-lg">Breakdown</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {yearCategories.map((cat, i) => {
                  const pct = totalSpend > 0 ? ((cat.value / totalSpend) * 100).toFixed(1) : '0.0';
                  return (
                    <div key={cat.key} className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium truncate">{cat.name}</span>
                          <span className="font-mono text-muted-foreground ml-2">{pct}%</span>
                        </div>
                        <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                        </div>
                      </div>
                      <span className="font-mono text-sm font-medium w-20 text-right shrink-0">{fmt(cat.value)}</span>
                    </div>
                  );
                })}
              </div>
              {totalSpend > 0 && (
                <div className="mt-4 pt-4 border-t flex justify-between text-sm font-semibold">
                  <span>Total</span>
                  <span className="font-mono">{fmt(totalSpend)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Monthly costs table */}
      {report && costMonths.length > 0 ? (
        <Card>
          <CardHeader><CardTitle className="text-lg">Monthly Costs</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Total Costs</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {costMonths.map((m) => {
                  const now = new Date();
                  const isCurrentMonth = m.monthNum === now.getMonth() + 1 && year === now.getFullYear();
                  return (
                    <TableRow key={m.month} className={cn(isCurrentMonth && 'bg-primary/5')}>
                      <TableCell className="font-medium">
                        {m.month_label}
                        {isCurrentMonth && <Badge className="ml-2 text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">Now</Badge>}
                      </TableCell>
                      <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                        {m.hasData ? fmt(m.expenses) : '\u2014'}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className="border-t-2 font-semibold bg-muted/30">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right font-mono text-red-600 dark:text-red-400">{fmt(costMonths.reduce((s, m) => s + m.expenses, 0))}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : !yearCategories.length ? (
        <Card>
          <CardContent className="py-16 text-center">
            <FileBarChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No cost data for {year}</h3>
          </CardContent>
        </Card>
      ) : null}

      {taxPaid && <TaxPaidPanel taxPaid={taxPaid} fmt={fmtTax} />}
    </div>
  );
}

// ── Main page ──

export function PersonalProfitLossPage() {
  const personalView = useEntityStore((s) => s.personalView);
  const toggleCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get('section') ?? 'pnl';
  const [year, setYear] = useState(new Date().getFullYear());
  const [report, setReport] = useState(null);
  const [priorYear, setPriorYear] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pnlError, setPnlError] = useState(null);
  const [fxRates, setFxRates] = useState(null);

  // /reports/entity-pnl (this page's data source) does not return an
  // fx_rates_used field, so fetch live rates independently, same pattern as
  // InvoicesPage.jsx's aggregate. GET /fx/rates?base=GBP returns GBP->currency
  // rates (e.g. EUR: 1.17); invert to currency->GBP before use, since that's
  // the shape convertAmount/currencyConvert.js expects.
  useEffect(() => {
    let cancelled = false;
    exchangeRatesAPI.getLatest().then((res) => {
      if (cancelled) return;
      const raw = res.data?.rates;
      if (!raw) return;
      setFxRates(
        Object.fromEntries(
          Object.entries(raw).map(([cur, rate]) => [cur, rate ? 1 / rate : rate])
        )
      );
    }).catch(() => {
      // Non-critical: cv() degrades to a pass-through until rates arrive.
    });
    return () => { cancelled = true; };
  }, []);

  const fetchData = async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    else setIsLoading(true);
    setPnlError(null);
    try {
      const results = await Promise.allSettled([
        reportsAPI.getEntityPnl({ year, scope: 'personal' }),
        reportsAPI.getEntityPnl({ year: year - 1, scope: 'personal' }),
      ]);
      if (results[0].status === 'fulfilled') {
        setReport(results[0].value.data);
      } else {
        setReport(null);
        const msg = getErrorMessage(results[0].reason);
        toast.error(msg);
        setPnlError(msg);
      }
      if (results[1].status === 'fulfilled') setPriorYear(results[1].value.data);
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(msg);
      setPnlError(msg);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, [year, personalView]);

  const handleSectionChange = (key) => {
    if (key === 'pnl') setSearchParams({});
    else setSearchParams({ section: key });
  };

  const handleRefresh = () => fetchData(true);

  if (isLoading) {
    return (
      <PersonalLayout>
        <div className="flex items-center justify-center h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PersonalLayout>
    );
  }

  if (pnlError && !report) {
    return (
      <PersonalLayout>
        <div className="flex flex-col gap-6" data-testid="personal-profit-loss-page">
          <Card>
            <CardContent className="py-16 text-center">
              <AlertTriangle className="h-12 w-12 mx-auto text-destructive mb-4" />
              <h3 className="text-lg font-medium mb-2">P&L failed to load</h3>
              <p className="text-muted-foreground">{pnlError}</p>
            </CardContent>
          </Card>
        </div>
      </PersonalLayout>
    );
  }

  return (
    <PersonalLayout>
      <div className="flex flex-col gap-6" data-testid="personal-profit-loss-page">
        {/* Section tabs */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div
            className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground w-fit"
            data-testid="pnl-section-tabs"
          >
            {SECTION_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleSectionChange(tab.key)}
                className={cn(
                  'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  section === tab.key && 'bg-background text-foreground shadow'
                )}
                data-testid={'pnl-section-tab-' + tab.key}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <CurrencyToggle />
        </div>

        {/* Section content */}
        {section === 'pnl' && (
          <PersonalPnlContent report={report} priorYear={priorYear} year={year} setYear={setYear} isRefreshing={isRefreshing} onRefresh={handleRefresh} toggleCurrency={toggleCurrency} fxRates={fxRates} />
        )}
        {section === 'revenue' && (
          <PersonalRevenueContent report={report} year={year} setYear={setYear} isRefreshing={isRefreshing} onRefresh={handleRefresh} toggleCurrency={toggleCurrency} fxRates={fxRates} />
        )}
        {section === 'costs' && (
          <PersonalCostsContent report={report} year={year} setYear={setYear} isRefreshing={isRefreshing} onRefresh={handleRefresh} toggleCurrency={toggleCurrency} fxRates={fxRates} />
        )}
      </div>
    </PersonalLayout>
  );
}
